import { LoaderCircle, RotateCw, ServerOff } from "lucide-react";
import type { ReactNode } from "react";
import { Navigate, useLocation, useParams } from "react-router";
import { Button } from "@/components/ui/button";
import { useSession } from "@/features/auth/session";
import { ForbiddenPage } from "@/features/system/ForbiddenPage";
import { RunsNeedComputer } from "@/features/system/RunsNeedComputer";
import { RuntimeStatePage } from "@/features/system/RuntimeStatePage";
import { useRuntime } from "./runtime";
import type { RouteDef } from "./routes";

function touchSmall(): boolean {
  return navigator.maxTouchPoints > 0 && window.innerWidth < 1024;
}

function SessionGate({ route, children }: { route: RouteDef; children: ReactNode }) {
  const s = useSession();
  const loc = useLocation();
  if (route.public) return <>{children}</>;
  if (s.status === "loading") {
    return (
      <div role="status" className="mt-24 flex justify-center text-muted-foreground">
        <LoaderCircle aria-hidden className="animate-spin" />
        <span className="sr-only">Loading</span>
      </div>
    );
  }
  if (s.status === "signed_out") return <Navigate to={`/sign-in?next=${encodeURIComponent(loc.pathname + loc.search)}`} replace />;
  if (s.status === "pending") return <Navigate to="/waiting" replace />;
  if (s.status === "no_access") return <Navigate to="/no-access" replace />;
  if (s.status === "identity_unavailable") {
    return (
      <section className="mx-auto mt-16 flex max-w-md flex-col items-center gap-4 text-center">
        <ServerOff aria-hidden className="size-10 text-muted-foreground" />
        <h1 className="text-2xl font-semibold">Can't confirm your access</h1>
        <p className="text-muted-foreground">
          The Admin API is unreachable and your cached role has expired. Check your connection, then retry.
        </p>
        <Button onClick={() => void s.refreshMe()}>
          <RotateCw aria-hidden /> Retry
        </Button>
      </section>
    );
  }
  if (route.roles && (!s.role || !route.roles.includes(s.role))) return <ForbiddenPage />;
  return <>{children}</>;
}

const SHARED_TWIN: Record<string, (p: { runId?: string; executionId?: string }) => string | null> = {
  run: (p) => (p.runId ? `/shared/runs/${p.runId}` : null),
  report: (p) => (p.runId ? `/shared/runs/${p.runId}/report` : null),
  execution: (p) => (p.runId && p.executionId ? `/shared/runs/${p.runId}/executions/${p.executionId}` : null),
};

function RuntimeGate({ route, children }: { route: RouteDef; children: ReactNode }) {
  const r = useRuntime();
  const params = useParams();
  const loc = useLocation();
  if (!route.needsRuntime) return <>{children}</>;
  if (r.state === "connected" || r.state === "recovering") return <>{children}</>;
  // Someone who opens a run link without ATS running (a developer, usually) gets the shared copy if there is one.
  const twin = SHARED_TWIN[route.id]?.(params);
  if (twin) return <Navigate to={twin + loc.search} replace />;
  if (touchSmall()) return <RunsNeedComputer />;
  return <RuntimeStatePage />;
}

export function wrapRoute(route: RouteDef, element: ReactNode) {
  return (
    <SessionGate route={route}>
      <RuntimeGate route={route}>{element}</RuntimeGate>
    </SessionGate>
  );
}

export function useCanRun(): { allowed: boolean; reason?: string } {
  const s = useSession();
  const r = useRuntime();
  if (s.role !== "TESTER" && s.role !== "ADMIN") return { allowed: false, reason: "Testers and admins can start runs." };
  if (r.state === "recovering")
    return { allowed: false, reason: "ATS is recovering after a restart. Starting or cancelling runs is paused." };
  if (r.state !== "connected") return { allowed: false, reason: "Start the ATS runtime first." };
  return { allowed: true };
}
